import type { FieldSelection } from '../protocol';
import { isTextField } from './cssCursor';

export interface NodeIdLookup {
    idOf(node: Node): number;
}

// rrweb records document selections but not the selection inside an input: this reports it so the agent sees it.
export class FieldSelectionTracker {
    private lastReported = 'null';
    private readonly nodes: NodeIdLookup;
    private readonly onChange: (selection: FieldSelection | null) => void;
    private readonly targetDocument: Document;

    constructor(nodes: NodeIdLookup, onChange: (selection: FieldSelection | null) => void, targetDocument: Document = document) {
        this.nodes = nodes;
        this.onChange = onChange;
        this.targetDocument = targetDocument;
        this.targetDocument.addEventListener('select', this.onSelect, true);
        this.targetDocument.addEventListener('selectionchange', this.onSelectionChange, true);
        this.targetDocument.addEventListener('input', this.onInput, true);
        this.targetDocument.addEventListener('focusout', this.onFocusOut, true);
    }

    destroy(): void {
        this.targetDocument.removeEventListener('select', this.onSelect, true);
        this.targetDocument.removeEventListener('selectionchange', this.onSelectionChange, true);
        this.targetDocument.removeEventListener('input', this.onInput, true);
        this.targetDocument.removeEventListener('focusout', this.onFocusOut, true);
    }

    private readonly onSelect = (event: Event): void => {
        const field = event.target as Element;

        if (isTextField(field)) {
            this.report(field, field.selectionStart ?? 0, field.selectionEnd ?? field.value.length);
        }
    };

    private readonly onSelectionChange = (): void => {
        const field = this.targetDocument.activeElement;

        if (field && isTextField(field) && field.selectionStart !== null && field.selectionEnd !== null) {
            this.report(field, field.selectionStart, field.selectionEnd);
        }
    };

    private readonly onInput = (event: Event): void => {
        const field = event.target as Element;

        if (isTextField(field) && field.selectionStart === null) {
            this.emit(null);
        }
    };

    private readonly onFocusOut = (): void => {
        this.emit(null);
    };

    private report(field: HTMLInputElement | HTMLTextAreaElement, start: number, end: number): void {
        const nodeId = this.nodes.idOf(field);

        this.emit(start === end || nodeId < 0 ? null : { nodeId, start, end });
    }

    private emit(selection: FieldSelection | null): void {
        const serialized = JSON.stringify(selection);

        if (serialized === this.lastReported) {
            return;
        }

        this.lastReported = serialized;
        this.onChange(selection);
    }
}
